import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import TemplateFormDialog from "@/components/TemplateFormDialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { FileText, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function DocumentTemplates() {
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ fields: [], doc_types: {} });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/document-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/document-templates/merge-fields").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const confirmDelete = async () => {
    try { await api.delete(`/document-templates/${deleteTarget.id}`); toast.success("Template deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="templates-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-[#002FA7]" /> Document Templates
          </h1>
          <p className="text-sm text-gray-500 mt-1">{templates.length} template{templates.length === 1 ? "" : "s"} · use merge fields to auto-fill client & case data</p>
        </div>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-template-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> New template
        </button>
      </div>

      {templates.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-16 border border-dashed border-gray-200 rounded-sm">No templates yet. Create your first template to generate documents from cases.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} data-testid={`template-card-${t.id}`}
              className="bg-white border border-gray-200 rounded-sm shadow-sm p-5 flex flex-col hover:border-[#002FA7]/40 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-heading font-bold text-gray-900 truncate">{t.name}</h3>
                  <span className="inline-block text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-sm mt-1">{meta.doc_types?.[t.doc_type] || t.doc_type}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => { setEditing(t); setDialogOpen(true); }} data-testid={`edit-template-${t.id}`}
                    className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setDeleteTarget(t)} data-testid={`delete-template-${t.id}`}
                    className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2 line-clamp-2 flex-1">{t.description || "—"}</p>
              <p className="text-xs text-gray-400 mt-3 font-mono line-clamp-2">{(t.body || "").slice(0, 120)}…</p>
            </div>
          ))}
        </div>
      )}

      <TemplateFormDialog open={dialogOpen} onOpenChange={setDialogOpen} template={editing}
        docTypes={meta.doc_types} mergeFields={meta.fields} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-template-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete template?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.name}</strong> will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-template-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
